import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faBars,
  faSignOutAlt,
  faLocationDot,
  faFileInvoice,
  faUserPlus,
  faUser,
  faQuestionCircle,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import { APP_ROUTES } from '../config/api.config';
import Footer from '../components/Footer';
import { useLogoutHandler } from '../utils/logoutHandler';
import LogoutConfirmDialog from '../components/LogoutConfirmDialog';
import Sidebar from '../components/Sidebar/Sidebar';
import logoMain from '../assets/images/logo-main.png';


const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f5f6fa;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const MobileHeader = styled.div`
  display: none;
`;

const HamburgerButton = styled.button`
  background: none;
  border: none;
  color: #333;
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.3s ease;
  border: 1px solid #e0e0e0;

  &:hover {
    background: rgba(255, 107, 107, 0.1);
  }
`;

const MobileLogo = styled.div`
  @media (max-width: 768px) {
    img {
      height: 32px;
      width: auto;
    }
  }
`;

const Overlay = styled.div<{ isVisible: boolean }>`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999;
    opacity: ${props => props.isVisible ? 1 : 0};
    visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
    transition: all 0.3s ease;
  }
`;

const Logo = styled.div<{ isCollapsed: boolean }>`
  padding: ${props => props.isCollapsed ? '0 10px' : '0 24px'};
  margin-bottom: 30px;
  text-align: ${props => props.isCollapsed ? 'center' : 'left'};

  img {
    max-width: ${props => props.isCollapsed ? '40px' : '160px'};
    height: auto;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const MenuList = styled.ul<{ isCollapsed: boolean }>`
  list-style: none;
  padding: 0;
  margin: 0;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
    flex-wrap: nowrap;
    background: #ff6b6b;
    padding: 12px 5px;
  }
`;

const MenuItem = styled.li<{ active?: boolean }>`
  margin: 8px 12px;
  border-radius: 8px;
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 768px) {
    margin: 4px;
  }
`;

const MenuLink = styled(Link) <{ isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  color: white;
  text-decoration: none;
  font-size: 16px;

  svg {
    width: 20px;
    margin-right: ${props => props.isCollapsed ? '0' : '12px'};
    font-size: 18px;
  }

  span {
    display: ${props => props.isCollapsed ? 'none' : 'block'};
    font-size: 16px;
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    
    span {
      display: none;
    }

    svg {
      margin-right: 0;
      font-size: 20px;
    }
  }
`;

const TopBar = styled.div<{ isCollapsed: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: white;
  border-radius: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 24px;
  position: fixed;
  top: 0;
  right: 0;
  left: ${props => props.isCollapsed ? '70px' : '240px'};
  z-index: 100;
  transition: left 0.3s ease;

  @media (max-width: 768px) {
    left: 0;
    width: 100%;
  }
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: #333;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.3s ease;
  border: 1px solid #e0e0e0;

  &:hover {
    background: rgba(255, 107, 107, 0.1);
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 107, 107, 0.1);
  }
  
  @media (max-width: 768px) {
    justify-content: flex-end;
    padding: 0;
    gap: 8px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const UserName = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #333;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #ff6b6b;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  margin-left: 8px;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const UserDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  display: ${props => props.isOpen ? 'block' : 'none'};
  z-index: 1000;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f8f9fa;
  }

  svg {
    width: 16px;
    margin-right: 12px;
  }
`;

// Existing InvoiceList styled components
const PageContainer = styled.div`
  padding: 20px;
  width: 100%;
  overflow-x: auto;
  margin: 0 20px;

  @media (max-width: 768px) {
    padding: 10px;
    margin: 0 15px;
  }
`;
// Add this with your other styled components
const LogoutMenuItem = styled.button<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  color: white;
  font-size: 15px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  svg {
    width: 20px;
    margin-right: ${props => props.$isCollapsed ? '0' : '12px'};
  }

  span {
    display: ${props => props.$isCollapsed ? 'none' : 'block'};
  }
`;

const FaqHeader = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 0;
  flex-wrap: wrap;
`;

const FaqIcon = styled.span`
  color: #ff4646;
  font-size: 2.7rem;
  font-weight: bold;
  line-height: 1;
`;

const FaqWord = styled.span`
  font-size: 2.3rem;
  font-weight: 700;
  color: #222;
  margin-left: 4px;
`;

const FaqSubtitle = styled.div`
  font-size: 1.15rem;
  color: #666;
  margin-top: 0.2rem;
  margin-bottom: 2.2rem;
  margin-left: 2.7rem;
  font-weight: 400;
`;

const Accordion = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const AccordionItem = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
  border: 1px solid #f1f1f1;
`;

const AccordionHeader = styled.button`
  width: 100%;
  background: none;
  border: none;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 28px;
  font-size: 1.15rem;
  font-weight: 600;
  color: #222;
  cursor: pointer;
  transition: background 0.2s;
  text-align: left;

  &:hover {
    background: #fff5f2;
  }
`;

const AccordionIcon = styled(FontAwesomeIcon)`
  font-size: 1.2rem;
  color: #ff6b6b;
`;

const AccordionPanel = styled.div`
  padding: 0 28px 22px 28px;
  color: #444;
  font-size: 1.05rem;
  background: #fff;
  border-top: 1px solid #f1f1f1;
`;

const MainContent = styled.div<{ isCollapsed: boolean }>`
  flex: 1;
  margin-left: ${props => props.isCollapsed ? '70px' : '240px'};
  padding: 0;
  padding-bottom: 60px;
  padding-top: 76px;
  transition: margin-left 0.3s ease;
  width: calc(100% - ${props => props.isCollapsed ? '70px' : '240px'});

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    padding-top: 76px;
  }
`;

const faqs = [
  {
    question: 'How long it will take to setup an account in WAGHL',
    answer: 'It will hardly take less than 10 minutes, and the entire process is automated. You can start using it immediately.'
  },
  {
    question: 'Is there any prerequisites before I start using the services',
    answer: 'You should not connect a newly registered WhatsApp Number. If you have recently registered WhatsApp on a phone number then you should warm up the number by sending and receiving messages from phone WhatsApp and should connect only after 2-3 weeks.'
  },
  {
    question: 'How many sub-accounts I can add under trial?',
    answer: 'You can add three sub-account under the trial period.'
  },
  {
    question: 'Is there any restrictions in trial period',
    answer: 'Yes, some features may be limited during the trial period. Please refer to our documentation for details.'
  },
  {
    question: 'Do we need WhatsApp Personal or Business WhatsApp to start using WAGHL services?',
    answer: 'You can use either WhatsApp Personal or Business account to start using WAGHL services.'
  }
];

const FAQ: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userName = user.name || '';
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    showLogoutConfirm,
    handleLogoutClick,
    handleLogoutConfirm,
    handleLogoutCancel
  } = useLogoutHandler();

  const handleLogout = () => {
    handleLogoutClick();
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  return (
    <DashboardContainer>
      <Sidebar
        isCollapsed={isCollapsed}
        isMobileOpen={isMobileOpen}
        onMobileClose={toggleMobileMenu}
      />

      <MainContent isCollapsed={isCollapsed}>
        <TopBar isCollapsed={isCollapsed}>
          <ToggleButton onClick={() => {
            if (window.innerWidth <= 768) {
              toggleMobileMenu();
            } else {
              setIsCollapsed(!isCollapsed);
            }
          }}>
            <FontAwesomeIcon icon={faBars} />
          </ToggleButton>
          <UserProfile ref={dropdownRef} onClick={() => setIsProfileOpen(!isProfileOpen)}>
            <UserInfo>
              <UserName>{userName}</UserName>
            </UserInfo>
            <UserAvatar>{userName.charAt(0)}</UserAvatar>
            <UserDropdown isOpen={isProfileOpen}>
              <DropdownItem onClick={() => navigate('/profile')}>
                <FontAwesomeIcon icon={faUser} />
                Profile
              </DropdownItem>
              <DropdownItem onClick={handleLogout}>
                <FontAwesomeIcon icon={faSignOutAlt} />
                Logout
              </DropdownItem>
            </UserDropdown>
          </UserProfile>
        </TopBar>

        <PageContainer>
          <FaqHeader>
            <FaqIcon>?</FaqIcon>
            <FaqWord>FAQ</FaqWord>
          </FaqHeader>
          <FaqSubtitle>Frequently Asked Questions</FaqSubtitle>
          <Accordion>
            {faqs.map((faq, idx) => (
              <AccordionItem key={idx}>
                <AccordionHeader
                  onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                  aria-expanded={openIndex === idx}
                  aria-controls={`faq-panel-${idx}`}
                  id={`faq-header-${idx}`}
                >
                  <span style={{ fontWeight: openIndex === idx ? 700 : 500 }}>
                    {faq.question}
                  </span>
                  <AccordionIcon icon={openIndex === idx ? faChevronUp : faChevronDown} />
                </AccordionHeader>
                {openIndex === idx && (
                  <AccordionPanel
                    id={`faq-panel-${idx}`}
                    aria-labelledby={`faq-header-${idx}`}
                  >
                    {faq.answer}
                  </AccordionPanel>
                )}
              </AccordionItem>
            ))}
          </Accordion>
        </PageContainer>
      </MainContent>
      <Footer isCollapsed={isCollapsed} />

      <LogoutConfirmDialog
        isOpen={showLogoutConfirm}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
      />
    </DashboardContainer>
  );
};

export default FAQ;